import { useMemo, useState } from 'react'
import {
  Badge,
  Button,
  Card,
  CardContent,
  DataTable,
  LoadingSpinner,
  toast,
  type DataTableColumn,
} from '@atrvd/saas-ui'
import { AlertTriangle, Plus } from 'lucide-react'
import {
  useDeleteBankAccount,
  useFacilities,
  usePacks,
  useSetManualIncludeInCashPosition,
} from '@/hooks/useBanking'
import { useTableColumnPrefs } from '@/hooks/useTableColumnPrefs'
import { ConfirmationDialog } from '@/components/admin/ConfirmationDialog'
import { CashPositionToggle } from '@/components/financial-accounts/CashPositionToggle'
import { HeaderWithHint } from '@/components/financial-accounts/HeaderWithHint'
import { ReadOnlyActionMenu, ReadOnlyTooltip } from '@/components/financial-accounts/ReadOnlyControls'
import {
  facilityAccountsEmpty,
  type FacilityFinancialAccountsScope,
} from '@/components/financial-accounts/financialAccountsScope'
import {
  CONNECTION_COPY,
  EM_DASH,
  accountConfirmLabel,
  facilityFilterOptions,
  packFilterOptions,
  pruneFacilitySelection,
} from '@/components/ledgersync/connectionManagement.utils'
import { EditManualAccountDialog } from './EditManualAccountDialog'
import { ManualAccountFilters } from './ManualAccountFilters'
import { ManualBankFormDialog } from './ManualBankFormDialog'
import { SortHeader } from './SortHeader'
import { MANUAL_BANK_COPY, accountsCountLabel } from './manualBankCopy'
import {
  byCashPosition,
  byFacilityThenNumber,
  byText,
  twoWaySort,
  type TableSort,
} from './manualBankSort'
import {
  filterManualAccountRows,
  hasActiveFilters,
  toManualAccountTableRows,
  type ManualAccountTableRow,
  type ManualBank,
} from './manualBanks.utils'

interface ManualBankAccountsTableProps {
  /** The bank; on a facility's page, `accounts` holds only that facility's. */
  bank: ManualBank
  /** The facility the page is scoped to; null on the organization-wide page. */
  scope: FacilityFinancialAccountsScope | null
  /**
   * Called after the last account listed here was deleted — or, on a
   * facility's page, moved to another facility.
   */
  onLastAccountRemoved: () => void
}

const DEFAULT_SORT: TableSort[] = [{ columnKey: 'facility', direction: 'asc' }]
/** A facility's page lists one facility, so its rows read by number. */
const SCOPED_DEFAULT_SORT: TableSort[] = [{ columnKey: 'number', direction: 'asc' }]

/**
 * Accounts & cards of one manual bank: search, Pack and Facility filters, the
 * Include in Cash Position toggle, and per-row Edit / Delete. The manual
 * counterpart of `ConnectionAccountsTable`, without status filter, balance or
 * bulk actions. The toggle saves on change, with no confirmation.
 *
 * On a facility's page the table drops the Pack column and the Pack/Facility
 * filters, sorts by number, prefills the facility for new accounts, and — for
 * an inactive facility — disables every change.
 *
 * Everything is client-side: a manual bank's accounts are already in memory,
 * so filtering and sorting need no server round trip. A bank with no accounts
 * (its last one was just deleted) shows only the empty state and the Add button.
 */
export function ManualBankAccountsTable({ bank, scope, onLastAccountRemoved }: ManualBankAccountsTableProps) {
  const scoped = scope !== null
  const readOnly = scope?.readOnly ?? false
  const [search, setSearch] = useState('')
  /** Raw MultiSelect values; may contain the `NO_PACK` sentinel. */
  const [packFilter, setPackFilter] = useState<string[]>([])
  /** Raw MultiSelect values; may contain the `UNASSIGNED_FACILITY` sentinel. */
  const [facilityFilter, setFacilityFilter] = useState<string[]>([])
  // Local state, so leaving the page and coming back restores the default sort.
  const [sort, setSort] = useState<TableSort[]>(scope ? SCOPED_DEFAULT_SORT : DEFAULT_SORT)
  const [addOpen, setAddOpen] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [deleteId, setDeleteId] = useState<string | null>(null)

  // Every facility for name lookup; only active ones are offered in the filter.
  const facilitiesQuery = useFacilities({ activeOnly: false })
  const packsQuery = usePacks()
  const allFacilities = facilitiesQuery.data
  const packs = packsQuery.data
  // Without them every row would read "Unassigned" / no pack — wrong, not empty.
  // Only a lookup that never produced data counts: a failed background refetch
  // keeps its cached data. Both can fail independently; show every message.
  // A facility's page shows no packs, so it does not wait for them.
  const lookupErrors = [
    allFacilities ? null : facilitiesQuery.error,
    packs || scoped ? null : packsQuery.error,
  ].filter((error) => error !== null)
  const lookupLoading = facilitiesQuery.isLoading || (!scoped && packsQuery.isLoading)
  const deleteAccount = useDeleteBankAccount()
  const setIncludeInCashPosition = useSetManualIncludeInCashPosition()

  const facilities = useMemo(() => allFacilities ?? [], [allFacilities])
  const activeFacilities = useMemo(() => facilities.filter((f) => f.is_active), [facilities])
  const packOptions = useMemo(() => packFilterOptions(packs ?? []), [packs])
  const facilityOptions = useMemo(
    () => facilityFilterOptions(activeFacilities, packFilter),
    [activeFacilities, packFilter],
  )

  const tableRows = useMemo(
    () => toManualAccountTableRows(bank.accounts, facilities, packs ?? []),
    [bank.accounts, facilities, packs],
  )
  const filters = { search, packs: packFilter, facilities: facilityFilter }
  const filtered = hasActiveFilters(filters)
  const visibleRows = filterManualAccountRows(tableRows, filters)

  const editTarget = bank.accounts.find((account) => account.id === editId) ?? null
  const deleteTarget = bank.accounts.find((account) => account.id === deleteId) ?? null

  /** A facility picked before a pack must not linger as an invisible filter. */
  const handlePackFilterChange = (next: string[]) => {
    setPackFilter(next)
    setFacilityFilter((current) =>
      pruneFacilitySelection(current, facilityFilterOptions(activeFacilities, next)),
    )
  }

  const clearFilters = () => {
    setSearch('')
    setPackFilter([])
    setFacilityFilter([])
  }

  const confirmDelete = () => {
    if (!deleteTarget) return
    // Judged before the write: after the refetch the bank has no accounts left.
    const wasLast = bank.accounts.length === 1
    deleteAccount.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast.success(`"${deleteTarget.name}" deleted`)
        setDeleteId(null)
        if (wasLast) onLastAccountRemoved()
      },
      onError: (error) => toast.error(error.message),
    })
  }

  const columns: DataTableColumn<ManualAccountTableRow>[] = useMemo(() => {
    const header = (label: string, columnKey: string) => ({
      header: <SortHeader label={label} columnKey={columnKey} sort={sort} />,
      headerLabel: label,
    })
    const all: DataTableColumn<ManualAccountTableRow>[] = [
      {
        key: 'name',
        ...header('Name', 'name'),
        sortable: true,
        disableHide: true,
        cellClassName: 'font-medium',
        sortFn: byText((row) => row.account.name),
        render: (row) => row.account.name,
      },
      {
        key: 'number',
        ...header('Account / Card number', 'number'),
        sortable: true,
        sortFn: byText((row) => row.account.account_number ?? ''),
        render: (row) => <span className="tabular-nums text-muted-foreground">{row.numberLabel}</span>,
      },
      {
        key: 'type',
        ...header('Type', 'type'),
        sortable: true,
        sortFn: byText((row) => row.typeLabel),
        cellClassName: 'text-slate-700',
        render: (row) => row.typeLabel,
      },
      {
        key: 'facility',
        ...header('Facility', 'facility'),
        sortable: true,
        sortFn: byFacilityThenNumber,
        cellClassName: (row) => (row.facilityAssigned ? 'text-slate-700' : 'text-slate-400'),
        render: (row) => row.facilityName,
      },
      {
        key: 'pack',
        ...header('Pack', 'pack'),
        sortable: true,
        sortFn: byText((row) => row.packName ?? ''),
        render: (row) => <span className="text-sm text-slate-600">{row.packName ?? EM_DASH}</span>,
      },
      {
        key: 'include_in_cash_position',
        header: (
          <HeaderWithHint label="Include in Cash Position" hint={CONNECTION_COPY.cashPositionHint}>
            <SortHeader label="Include in Cash Position" columnKey="include_in_cash_position" sort={sort} />
          </HeaderWithHint>
        ),
        headerLabel: 'Include in Cash Position',
        // Shown to users whose saved column layout predates this column, too:
        // without it `useTableColumnPrefs` adds a new column hidden.
        defaultVisible: true,
        sortable: true,
        sortFn: byCashPosition,
        render: (row) => (
          <CashPositionToggle
            accountName={row.account.name}
            checked={row.account.include_in_cash_position}
            locked={row.cashPositionLocked}
            busy={
              setIncludeInCashPosition.isPending &&
              setIncludeInCashPosition.variables?.ids.includes(row.account.id) === true
            }
            readOnly={readOnly}
            onChange={(include) => setIncludeInCashPosition.mutate({ ids: [row.account.id], include })}
          />
        ),
      },
      {
        key: 'actions',
        header: '',
        headerLabel: 'Actions',
        align: 'right',
        width: '56px',
        disableHide: true,
        // DataTable ignores `align`, so the cell aligns itself.
        render: (row) => (
          <div className="flex justify-end">
            <ReadOnlyActionMenu
              readOnly={readOnly}
              ariaLabel={`Actions for ${row.account.name}`}
              items={[
                {
                  id: 'edit',
                  label: 'Edit',
                  onClick: () => setEditId(row.account.id),
                },
                {
                  id: 'delete',
                  label: 'Delete',
                  variant: 'danger' as const,
                  onClick: () => setDeleteId(row.account.id),
                },
              ]}
            />
          </div>
        ),
      },
    ]
    // Scoped, the Pack column goes: every row shares the facility, so it
    // would repeat one value.
    return scoped ? all.filter((column) => column.key !== 'pack') : all
  }, [sort, scoped, readOnly, setIncludeInCashPosition])

  // A table of its own on facility pages: it has no Pack column, and sharing
  // the prefs would drop Pack from the organization-wide table's layout.
  const columnPrefs = useTableColumnPrefs(
    scoped ? 'manual-bank-accounts-facility' : 'manual-bank-accounts',
    columns,
  )
  const name = bank.institutionName

  const body = lookupLoading ? (
    <div className="flex justify-center py-8">
      <LoadingSpinner />
    </div>
  ) : lookupErrors.length > 0 ? (
    <div
      role="alert"
      className="flex items-center gap-2 rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive"
    >
      <AlertTriangle aria-hidden="true" className="h-4 w-4 shrink-0" />
      {lookupErrors.map((error) => error.message).join('; ')}
    </div>
  ) : (
    <DataTable<ManualAccountTableRow>
      columns={columns}
      data={visibleRows}
      rowKey={(row) => row.account.id}
      sorting={{
        mode: 'client',
        sortState: sort,
        onSortChange: (next) => setSort((current) => twoWaySort(current, next)),
      }}
      emptyState={
        <p className="text-sm text-muted-foreground">{MANUAL_BANK_COPY.accountsEmptyFiltered}</p>
      }
      itemName="accounts"
      showColumnControls
      enableColumnReorder
      {...columnPrefs}
    />
  )

  return (
    <Card>
      <CardContent className="space-y-4 p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-bold tracking-tight">Accounts &amp; cards</h3>
            {/* The rows on screen, so it follows the search and filters. */}
            <Badge variant="muted">{accountsCountLabel(visibleRows.length)}</Badge>
          </div>
          {/* New accounts need a name to live under; the unnamed group has none. */}
          {name !== null && (
            <ReadOnlyTooltip readOnly={readOnly}>
              <Button onClick={() => setAddOpen(true)} disabled={readOnly}>
                <Plus aria-hidden="true" className="h-4 w-4" />
                {MANUAL_BANK_COPY.addAccountCta}
              </Button>
            </ReadOnlyTooltip>
          )}
        </div>

        {bank.accounts.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {/* The bank may still have accounts this page does not list: at other
                facilities, or at inactive ones. */}
            {scope ? facilityAccountsEmpty(scope.facilityName) : MANUAL_BANK_COPY.accountsEmpty}
          </p>
        ) : (
          <>
            <ManualAccountFilters
              search={search}
              onSearchChange={setSearch}
              packFacilityFilters={
                scoped
                  ? null
                  : {
                      packOptions,
                      packs: packFilter,
                      onPacksChange: handlePackFilterChange,
                      facilityOptions,
                      facilities: facilityFilter,
                      onFacilitiesChange: setFacilityFilter,
                    }
              }
              onClear={filtered ? clearFilters : null}
            />
            {body}
          </>
        )}
      </CardContent>

      {addOpen && name !== null && (
        <ManualBankFormDialog
          mode={{ kind: 'add-accounts', institutionName: name, defaultFacilityId: scope?.facilityId ?? null }}
          onClose={() => setAddOpen(false)}
        />
      )}
      {editTarget && (
        <EditManualAccountDialog
          account={editTarget}
          bankName={bank.displayName}
          onClose={() => setEditId(null)}
          onSaved={(values) => {
            // Moved to another facility: it leaves this facility's table.
            if (scope && values.facility_id !== scope.facilityId && bank.accounts.length === 1) {
              onLastAccountRemoved()
            }
          }}
        />
      )}
      <ConfirmationDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open && !deleteAccount.isPending) setDeleteId(null)
        }}
        title={deleteTarget ? `Delete ${accountConfirmLabel(deleteTarget)}?` : 'Delete'}
        description={MANUAL_BANK_COPY.deleteAccountDescription}
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
        isLoading={deleteAccount.isPending}
        onConfirm={confirmDelete}
      />
    </Card>
  )
}
